import { useEffect, useRef, useState } from "react";
import { IonIcon, IonSelect, IonSelectOption, IonRadioGroup, IonRadio } from "@ionic/react";
import { createOutline, trashOutline, resizeOutline } from "ionicons/icons";
import { fieldMeta, roleColor, roleLabel, todayLong, displayValue } from "@/utils/signDocFields";
import { DatePopover } from "@/components/DateInput";
import PreviewPager from "@/components/PreviewPager";

// An uploaded PDF's pages, each with its fields drawn over it. Three modes:
//   edit - the sender places fields: drag to move, the corner to resize, tap
//          to select (then edit or delete); a tap on the page while a field
//          type is armed (or a drop from the palette) places one there.
//   fill - a signer fills in their own fields; everyone else's show what was
//          already filled, or a faint placeholder for those still to sign.
//   view - every value as it stands, read only.
// Positions are fractions of the page, so the boxes track the page however
// wide it is drawn.
// The pages stack down the screen (the editor), or with `paged` they ride
// the preview modal's PreviewPager: one page at a time, the "Page X of Y"
// range slider and a swipe turning them (the signing page). `page` and
// `onPageChange` hold the shown page from outside; alone, it keeps its own.

const alpha = (hex, a) => {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

function PageBox({ index, src, children, onPlace, placing, onDropType, paged = false }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const at = (clientX, clientY) => {
    const r = ref.current.getBoundingClientRect();
    return [(clientX - r.left) / r.width, (clientY - r.top) / r.height];
  };
  return (
    <div className="sdoc-page-wrap">
      {!paged && <div className="sdoc-page-num">Page {index + 1}</div>}
      <div
        ref={ref}
        className={`sdoc-page${placing ? " is-placing" : ""}`}
        onClick={(e) => { if (placing && onPlace && !e.target.closest(".sdoc-field")) { const [x, y] = at(e.clientX, e.clientY); onPlace(index, x, y); } }}
        onDragOver={(e) => { if (onDropType) e.preventDefault(); }}
        onDrop={(e) => {
          if (!onDropType) return;
          e.preventDefault();
          const type = e.dataTransfer.getData("text/sign-field");
          if (type) { const [x, y] = at(e.clientX, e.clientY); onDropType(type, index, x, y); }
        }}
      >
        <img src={src} alt={`Page ${index + 1}`} draggable={false} />
        {children(size)}
      </div>
    </div>
  );
}

export default function SignDocCanvas({
  pages, fields, roles, mode = "view", values = {}, activeRole = null,
  selectedId = null, onSelect, onChange, onEditField, onDeleteField,
  placing = false, onPlace, onDropType, onSignField, onValue,
  paged = false, page = null, onPageChange = null,
}) {
  // the page on show in paged mode: held outside when a parent wants to turn it
  const [ownPage, setOwnPage] = useState(0);
  const pageIdx = Math.min(page ?? ownPage, Math.max(0, pages.length - 1));
  const turnPage = (i) => { setOwnPage(i); onPageChange && onPageChange(i); };
  // a signer's Date field: the app's calendar popover, anchored to the box
  const [dateMenu, setDateMenu] = useState({ open: false, event: undefined, fieldId: null });

  // ── edit: dragging a field or its corner ──
  const drag = useRef(null);
  // a double tap opens the field's settings: two taps on one field in quick
  // succession, so it works for a finger as well as a mouse (touch screens
  // never send dblclick, and a field being dragged swallows the mouse's)
  const lastTap = useRef({ id: null, t: 0 });
  const startDrag = (e, f, kind) => {
    if (mode !== "edit") return;
    e.stopPropagation();
    const pageEl = e.currentTarget.closest(".sdoc-page");
    const rect = pageEl.getBoundingClientRect();
    drag.current = { id: f.id, kind, sx: e.clientX, sy: e.clientY, f: { ...f }, rect, moved: false };
    onSelect && onSelect(f.id);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const moveDrag = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / d.rect.width;
    const dy = (e.clientY - d.sy) / d.rect.height;
    if (Math.abs(dx) + Math.abs(dy) > 0.002) d.moved = true;
    const f = d.f;
    const next = d.kind === "move"
      ? { ...f, x: Math.min(1 - f.w, Math.max(0, f.x + dx)), y: Math.min(1 - f.h, Math.max(0, f.y + dy)) }
      : { ...f, w: Math.min(1 - f.x, Math.max(0.02, f.w + dx)), h: Math.min(1 - f.y, Math.max(0.015, f.h + dy)) };
    onChange && onChange(fields.map((x) => (x.id === f.id ? next : x)));
  };
  const lastMoved = useRef(false); // whether the last press dragged (a drag is not a tap)
  const endDrag = () => { if (drag.current) lastMoved.current = drag.current.moved; drag.current = null; };

  const fieldBody = (f, pageSize, own) => {
    const v = f.role === "sender" ? (values[f.id] ?? f.value) : values[f.id];
    const byHeight = Math.max(9, Math.min(15, f.h * pageSize.h * 0.6));
    // a value fits its box on one line: shrink it for longer text
    const fit = (text) => Math.max(7, Math.min(byHeight, (f.w * pageSize.w - 8) / (Math.max(String(text || "").length, 4) * 0.55)));
    const fontSize = byHeight;
    const meta = fieldMeta(f.type);
    // a filled value shows as itself
    if ((f.type === "signature" || f.type === "initials") && v) {
      return <img src={v} alt={meta.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} draggable={false} />;
    }
    if (mode === "fill" && own) {
      if (f.type === "signature" || f.type === "initials") {
        return <span className="sdoc-cta" style={{ fontSize }}>{f.type === "signature" ? "Sign" : "Initial"}</span>;
      }
      if (f.type === "date") return <span className="sdoc-text" style={{ fontSize: fit(todayLong(f.format)) }}>{todayLong(f.format)}</span>;
      if (f.type === "pickdate") {
        const shown = v ? displayValue(f, v) : (f.label || "Pick a date");
        return <span className={v ? "sdoc-text" : "sdoc-cta"} style={{ fontSize: fit(shown) }}>{shown}</span>;
      }
      if (f.type === "text" || f.type === "number") {
        return (
          <input className="sdoc-input" style={{ fontSize }} value={v ?? ""} placeholder={f.label || meta.label}
            inputMode={f.type === "number" ? "decimal" : "text"}
            onChange={(e) => onValue && onValue(f.id, f.type === "number" ? e.target.value.replace(/[^\d.,-]/g, "") : e.target.value)} />
        );
      }
      if (f.type === "checkbox") return <span style={{ fontSize: fontSize * 1.1, fontWeight: 700 }}>{v ? "✓" : ""}</span>;
      if (f.type === "dropdown") {
        return (
          <IonSelect className="sdoc-select" interface="popover" placeholder={f.label || "Choose"} value={v || ""} aria-label={f.label || "Choose"}
            onIonChange={(e) => onValue && onValue(f.id, e.detail.value)} style={{ fontSize }}>
            {(f.options || []).map((o) => <IonSelectOption key={o} value={o}>{o}</IonSelectOption>)}
          </IonSelect>
        );
      }
      if (f.type === "radio") {
        return (
          <IonRadioGroup value={v || ""} onIonChange={(e) => onValue && onValue(f.id, e.detail.value)} className="sdoc-radios">
            {(f.options || []).map((o) => (
              <IonRadio key={o} value={o} labelPlacement="end" justify="start" style={{ fontSize }}>{o}</IonRadio>
            ))}
          </IonRadioGroup>
        );
      }
    }
    const shown = displayValue(f, f.type === "date" && mode === "fill" && own ? todayLong(f.format) : v);
    if (shown) return <span className="sdoc-text" style={{ fontSize: f.type === "checkbox" ? fontSize : fit(shown), fontWeight: f.type === "checkbox" ? 700 : 400 }}>{shown}</span>;
    // empty: what the box is, and whose
    return (
      <span className="sdoc-ph" style={{ fontSize: Math.max(8, fontSize * 0.8) }}>
        <IonIcon icon={meta.icon} />
        {f.label || meta.label}
      </span>
    );
  };

  // one page, its fields drawn over it
  const pageBox = (src, pi) => (
        <PageBox key={pi} index={pi} src={src} paged={paged} placing={placing} onPlace={onPlace} onDropType={mode === "edit" ? onDropType : null}>
          {(pageSize) => fields.filter((f) => f.page === pi).map((f) => {
            const color = roleColor(roles, f.role);
            const own = mode === "fill" && f.role === activeRole;
            const rawV = f.role === "sender" ? (values[f.id] ?? f.value) : values[f.id];
            const filled = rawV !== undefined && rawV !== null && rawV !== "" && rawV !== false;
            const selected = mode === "edit" && selectedId === f.id;
            const style = {
              left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`,
              borderColor: mode === "view" && filled ? "transparent" : (own || mode === "edit" ? color : alpha(color, 0.35)),
              background: mode === "edit" ? alpha(color, selected ? 0.22 : 0.14) : own ? alpha(color, 0.1) : "transparent",
              borderStyle: mode === "fill" && !own ? "dashed" : "solid",
            };
            return (
              <div key={f.id}
                className={`sdoc-field sdoc-${f.type}${selected ? " is-selected" : ""}${own ? " is-own" : ""}${mode === "edit" ? " is-edit" : ""}`}
                style={style}
                title={`${fieldMeta(f.type).label} · ${roleLabel(roles, f.role)}${f.required ? " · required" : ""}`}
                onPointerDown={(e) => startDrag(e, f, "move")}
                onClick={(e) => {
                  e.stopPropagation();
                  if (mode === "edit") {
                    const now = Date.now();
                    const moved = lastMoved.current;
                    lastMoved.current = false;
                    if (moved) { lastTap.current = { id: null, t: 0 }; onSelect && onSelect(f.id); return; }
                    if (e.detail >= 2 || lastTap.current.id === f.id && now - lastTap.current.t < 400) {
                      lastTap.current = { id: null, t: 0 };
                      if (onEditField) onEditField(f);
                      return;
                    }
                    lastTap.current = { id: f.id, t: now };
                    onSelect && onSelect(f.id);
                    return;
                  }
                  if (own && (f.type === "signature" || f.type === "initials")) onSignField && onSignField(f);
                  if (own && f.type === "checkbox") onValue && onValue(f.id, !values[f.id]);
                  if (own && f.type === "pickdate") setDateMenu({ open: true, event: e.nativeEvent, fieldId: f.id });
                }}
              >
                {fieldBody(f, pageSize, own)}
                {f.required && (own || mode === "edit") && <span className="sdoc-req" style={{ background: color }}>*</span>}
                {selected && (
                  <>
                    <div className="sdoc-tools" onPointerDown={(e) => e.stopPropagation()}>
                      <button type="button" aria-label="Edit field" title="Edit" onClick={(e) => { e.stopPropagation(); onEditField && onEditField(f); }}><IonIcon icon={createOutline} /></button>
                      <button type="button" aria-label="Delete field" title="Delete" className="is-danger" onClick={(e) => { e.stopPropagation(); onDeleteField && onDeleteField(f.id); }}><IonIcon icon={trashOutline} /></button>
                    </div>
                    <span className="sdoc-handle" style={{ background: color }} onPointerDown={(e) => startDrag(e, f, "resize")} aria-label="Resize">
                      <IonIcon icon={resizeOutline} />
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </PageBox>
  );

  return (
    <div className={`sdoc-pages${paged ? " is-paged" : ""}`} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
      {paged
        ? <PreviewPager pages={pages} index={pageIdx} onIndexChange={turnPage} altPrefix="Page" renderPage={pageBox} />
        : pages.map(pageBox)}
      <DatePopover menu={dateMenu} value={dateMenu.fieldId ? values[dateMenu.fieldId] : undefined}
        onClose={() => setDateMenu({ open: false, event: undefined, fieldId: null })}
        onSelect={(iso) => { if (dateMenu.fieldId && onValue) onValue(dateMenu.fieldId, iso); }} />
    </div>
  );
}
